import {
  createClerkSupabaseClientLandingZone,
  createRealtimeSupabaseClientLandingZone,
  validateSessionAndRetry,
} from '../../utility/utility'
import { toTagId } from './normalizeCatalogTag'
import { refreshOrgPinnedTagGroupName, syncTagsCatalog, upsertTag } from './tagsCatalogService'

export const TAGS_REALTIME_TOPIC_PREFIX = 'topic:tags'
const REALTIME_RETRY_DELAY_MS = 1000
const PINNED_GROUP_REFRESH_DEBOUNCE_MS = 300

export function getTagsCatalogRealtimeTopic(orgId) {
  if (!orgId) return null
  return `${TAGS_REALTIME_TOPIC_PREFIX}/${String(orgId).trim()}`
}

export function extractTagsCatalogBroadcastRecord(payload) {
  const body = payload?.payload ?? payload?.record ?? payload
  if (!body || typeof body !== 'object') return null
  if (body.new_record && typeof body.new_record === 'object') return body.new_record
  if (body.record && typeof body.record === 'object' && Object.keys(body.record).length > 0) {
    return body.record
  }
  if (body.new && typeof body.new === 'object' && Object.keys(body.new).length > 0) {
    return body.new
  }
  if (body.old_record && typeof body.old_record === 'object') return body.old_record
  if (body.old && typeof body.old === 'object') return body.old
  return body
}

/**
 * Apply a tags-table broadcast payload to the in-memory catalog and IndexedDB.
 * Caller must gate on CC-6037 (`isShipmentsTagPivotEnabled`) — see Header.jsx.
 * DELETE events soft-archive (`deleted: true`) so shipments can still resolve tag metadata.
 * @param {string} orgId
 * @param {'INSERT' | 'UPDATE' | 'DELETE'} eventType
 * @param {object | null | undefined} record
 */
export function applyTagsCatalogRealtimeEvent(orgId, eventType, record) {
  const trimmedOrgId = String(orgId || '').trim()
  if (!trimmedOrgId || !record) return

  const recordOrgId = String(record?.org_id ?? '').trim()
  if (recordOrgId && recordOrgId !== trimmedOrgId) return

  const tagId = toTagId(record)
  if (!tagId) return

  if (eventType === 'INSERT' || eventType === 'UPDATE' || eventType === 'DELETE') {
    upsertTag(trimmedOrgId, eventType === 'DELETE' ? { ...record, deleted: true } : record)
  }
}

/**
 * Subscribe to org-scoped tags table realtime broadcasts on Landing Zone.
 * Caller must gate on CC-6037 (`isShipmentsTagPivotEnabled`) — see Header.jsx.
 * @param {object} session Clerk session
 * @param {string} orgId Organization id
 * @param {{ syncOnSubscribe?: boolean, refreshPinnedGroupOnCachedSync?: boolean }} [options]
 * @returns {() => void} unsubscribe
 */
export function subscribeTagsCatalogRealtime(session, orgId, options = {}) {
  if (!session || !orgId) {
    return () => {}
  }

  const trimmedOrgId = String(orgId).trim()
  const topic = getTagsCatalogRealtimeTopic(trimmedOrgId)
  if (!topic) {
    return () => {}
  }

  const landingZoneClient = createClerkSupabaseClientLandingZone(session)
  const realtimeClient = createRealtimeSupabaseClientLandingZone(session)
  const syncOnSubscribe = options.syncOnSubscribe !== false
  const refreshPinnedGroupOnCachedSync = options.refreshPinnedGroupOnCachedSync !== false

  let active = true
  let channel = null
  let retryTimeout = null
  let pinnedRefreshTimeout = null

  const removeExistingChannelsForTopic = async () => {
    const existingChannels =
      realtimeClient
        .getChannels()
        ?.filter(
          (existingChannel) =>
            existingChannel?.topic === topic || existingChannel?.topic === `realtime:${topic}`,
        ) ?? []

    await Promise.all(
      existingChannels.map((existingChannel) => realtimeClient.removeChannel(existingChannel)),
    )
  }

  const teardownChannel = async () => {
    if (!channel) return
    const channelToRemove = channel
    channel = null
    await realtimeClient.removeChannel(channelToRemove)
  }

  const schedulePinnedGroupRefresh = () => {
    clearTimeout(pinnedRefreshTimeout)
    pinnedRefreshTimeout = setTimeout(() => {
      if (!active) return
      refreshOrgPinnedTagGroupName(landingZoneClient, trimmedOrgId).catch((refreshError) => {
        console.error('Failed to refresh pinned tag group after tags realtime:', refreshError)
      })
    }, PINNED_GROUP_REFRESH_DEBOUNCE_MS)
  }

  const applyBroadcast = (eventType, payload) => {
    applyTagsCatalogRealtimeEvent(
      trimmedOrgId,
      eventType,
      extractTagsCatalogBroadcastRecord(payload),
    )
    schedulePinnedGroupRefresh()
  }

  const subscribe = async () => {
    if (!active) return

    await removeExistingChannelsForTopic()
    if (!active) return

    channel = realtimeClient.channel(topic, {
      config: {
        private: true,
        broadcast: { self: true },
      },
    })

    channel
      .on('broadcast', { event: 'INSERT' }, (payload) => applyBroadcast('INSERT', payload))
      .on('broadcast', { event: 'UPDATE' }, (payload) => applyBroadcast('UPDATE', payload))
      .on('broadcast', { event: 'DELETE' }, (payload) => applyBroadcast('DELETE', payload))
      .subscribe((status, error) => {
        if (status === 'SUBSCRIBED' && syncOnSubscribe) {
          syncTagsCatalog(landingZoneClient, trimmedOrgId, {
            refreshPinnedGroupOnCachedSync,
          }).catch((syncError) => {
            console.error('Failed to sync tags catalog after realtime subscribe:', syncError)
          })
        }

        validateSessionAndRetry({ topic, status, error }, status, session, () => {
          if (!active) return

          clearTimeout(retryTimeout)
          retryTimeout = setTimeout(async () => {
            if (!active) return
            await teardownChannel()
            syncTagsCatalog(landingZoneClient, trimmedOrgId, {
              refreshPinnedGroupOnCachedSync,
            }).catch((syncError) => {
              console.error('Failed to sync tags catalog after realtime retry:', syncError)
            })
            await subscribe()
          }, REALTIME_RETRY_DELAY_MS)
        })
      })
  }

  void subscribe()

  return () => {
    active = false
    clearTimeout(retryTimeout)
    clearTimeout(pinnedRefreshTimeout)
    void teardownChannel()
  }
}
